import React, { useState, useRef, useEffect } from 'react';
import { 
  Play, 
  Upload, 
  Download, 
  Save, 
  Maximize2, 
  Minimize2, 
  Settings, 
  Copy, 
  RotateCcw,
  FileText,
  Zap,
  Shield,
  Smartphone,
  Globe,
  Code,
  CheckCircle,
  Eye,
  ChevronDown,
  AlertCircle,
  Info
} from 'lucide-react';

interface MinifierOptions {
  removeComments: boolean;
  removeWhitespace: boolean;
  removeEmptyRules: boolean;
  optimizeColors: boolean;
  optimizeShorthands: boolean;
  removeUnusedSelectors: boolean;
}

interface CodeIssue {
  line: number;
  type: 'info' | 'warning' | 'error';
  message: string;
}

const CSSMinifier: React.FC = () => {
  const [inputCSS, setInputCSS] = useState<string>(`/* Advanced CSS Minifier Demo */
.container {
  margin: 0 auto;
  padding: 20px 20px 20px 20px;
  background-color: #ffffff;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  max-width: 1200px;
}

.button {
  background-color: #3b82f6;
  color: white;
  padding: 10px 20px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.3s ease;
}

.button:hover {
  background-color: #2563eb;
}

/* Empty rule that will be removed */
.unused-class {
}

.text-primary {
  color: #111827;
  font-weight: 600;
  line-height: 1.5;
}`);
  const [outputCSS, setOutputCSS] = useState<string>('');
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [viewMode, setViewMode] = useState<'code' | 'preview' | 'split'>('split');
  const [compressionRatio, setCompressionRatio] = useState<number>(0);
  const [originalSize, setOriginalSize] = useState<number>(0);
  const [minifiedSize, setMinifiedSize] = useState<number>(0);
  const [lineCount, setLineCount] = useState<number>(0);
  const [charCount, setCharCount] = useState<number>(0);
  const [wordCount, setWordCount] = useState<number>(0);
  const [codeIssues, setCodeIssues] = useState<CodeIssue[]>([]);
  
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [options, setOptions] = useState<MinifierOptions>({
    removeComments: true,
    removeWhitespace: true,
    removeEmptyRules: true,
    optimizeColors: true,
    optimizeShorthands: true,
    removeUnusedSelectors: false
  });

  useEffect(() => {
    const lines = inputCSS.split('\n').length;
    const chars = inputCSS.length;
    const words = inputCSS.trim() ? inputCSS.trim().split(/\s+/).length : 0;
    
    setLineCount(lines);
    setCharCount(chars);
    setWordCount(words);
    
    // Analyze code issues
    const issues: CodeIssue[] = [];
    const cssLines = inputCSS.split('\n');
    
    cssLines.forEach((line, index) => {
      if (line.includes('padding:') && line.match(/padding:\s*(\d+\w+)\s+(\d+\w+)\s+\1\s+\2/)) {
        issues.push({
          line: index + 1,
          type: 'info',
          message: 'Consider using shorthand padding syntax'
        });
      }
      if (line.includes('{}') || (line.includes('{') && !line.includes('}') && cssLines[index + 1]?.trim() === '}')) {
        issues.push({
          line: index + 1,
          type: 'info',
          message: 'Empty CSS rule detected'
        });
      }
    });
    
    setCodeIssues(issues);
  }, [inputCSS]);

  const minifyCSS = (css: string): string => {
    let minified = css;

    if (options.removeComments) {
      minified = minified.replace(/\/\*[\s\S]*?\*\//g, '');
    }

    if (options.removeWhitespace) {
      minified = minified.replace(/\s+/g, ' ');
      minified = minified.replace(/;\s*}/g, '}');
      minified = minified.replace(/\s*{\s*/g, '{');
      minified = minified.replace(/;\s*/g, ';');
      minified = minified.replace(/,\s*/g, ',');
      minified = minified.replace(/:\s*/g, ':');
    }

    if (options.removeEmptyRules) {
      minified = minified.replace(/[^{}]*{\s*}/g, '');
    }

    if (options.optimizeColors) {
      minified = minified.replace(/#([0-9a-fA-F])\1([0-9a-fA-F])\2([0-9a-fA-F])\3/g, '#$1$2$3');
      minified = minified.replace(/rgb\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)/g, (match, r, g, b) => {
        const hex = '#' + [r, g, b].map(x => parseInt(x).toString(16).padStart(2, '0')).join('');
        return hex;
      });
    }

    if (options.optimizeShorthands) {
      minified = minified.replace(/padding:\s*(\d+\w+)\s+(\d+\w+)\s+\1\s+\2/g, 'padding:$1 $2');
      minified = minified.replace(/margin:\s*(\d+\w+)\s+(\d+\w+)\s+\1\s+\2/g, 'margin:$1 $2');
    }

    return minified.trim();
  };

  const highlightCSS = (css: string): string => {
    return css
      .replace(/\/\*[\s\S]*?\*\//g, '<span class="text-gray-400">$&</span>')
      .replace(/([.#][\w-]+)/g, '<span class="text-yellow-400">$1</span>')
      .replace(/([\w-]+)(?=\s*:)/g, '<span class="text-blue-400">$1</span>')
      .replace(/(:)(\s*[^;{}]+)/g, '$1<span class="text-green-400">$2</span>')
      .replace(/([{}])/g, '<span class="text-purple-400">$1</span>');
  };

  const handleMinify = (): void => {
    const minified = minifyCSS(inputCSS);
    setOutputCSS(minified);
    
    const originalBytes = new Blob([inputCSS]).size;
    const minifiedBytes = new Blob([minified]).size;
    const ratio = originalBytes > 0 ? ((originalBytes - minifiedBytes) / originalBytes * 100) : 0;
    
    setOriginalSize(originalBytes);
    setMinifiedSize(minifiedBytes);
    setCompressionRatio(Math.round(ratio * 100) / 100);
  };

  const handleUpload = (): void => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0];
    if (file && file.type === 'text/css') {
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as string;
        setInputCSS(content);
      };
      reader.readAsText(file);
    }
  };

  const handleDownload = (): void => {
    const blob = new Blob([outputCSS], { type: 'text/css' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'minified.css';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleSave = (): void => {
    localStorage.setItem('advanced-css-minifier-input', inputCSS);
    localStorage.setItem('advanced-css-minifier-output', outputCSS);
  };

  const handleCopy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(outputCSS);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const handleReset = (): void => {
    setInputCSS('');
    setOutputCSS('');
    setCompressionRatio(0);
    setOriginalSize(0);
    setMinifiedSize(0);
  };

  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className={`min-h-screen bg-gray-900 text-white ${isFullscreen ? 'fixed inset-0 z-50' : ''}`}>
      {/* Top Navigation */}
      <div className="bg-gray-800 border-b border-gray-700 px-4 py-2">
        <div className="flex items-center space-x-2 text-sm text-gray-400">
          <span>CATEGORY</span>
          <ChevronDown className="w-3 h-3 rotate-90" />
          <span>Minifiers</span>
          <ChevronDown className="w-3 h-3 rotate-90" />
          <span className="text-blue-400">CSS Minifier</span>
        </div>
      </div>

      {/* Main Header */}
      <div className="bg-gray-900 border-b border-gray-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-3">
              <Code className="w-6 h-6 text-purple-400" />
              <h1 className="text-xl font-semibold text-white">Advanced CSS Minifier</h1>
            </div>
            
            <div className="flex items-center bg-gray-800 rounded-lg border border-gray-700">
              <button
                onClick={() => setViewMode('code')}
                className={`px-4 py-2 text-sm font-medium rounded-l-lg transition-colors ${
                  viewMode === 'code' ? 'bg-gray-700 text-white' : 'text-gray-400 hover:text-white'
                }`}
              >
                <FileText className="w-4 h-4 inline mr-2" />
                Code
              </button>
              <button
                onClick={() => setViewMode('preview')}
                className={`px-4 py-2 text-sm font-medium transition-colors ${
                  viewMode === 'preview' ? 'bg-gray-700 text-white' : 'text-gray-400 hover:text-white'
                }`}
              >
                <Eye className="w-4 h-4 inline mr-2" />
                Preview
              </button>
              <button
                onClick={() => setViewMode('split')}
                className={`px-4 py-2 text-sm font-medium rounded-r-lg transition-colors ${
                  viewMode === 'split' ? 'bg-gray-700 text-white' : 'text-gray-400 hover:text-white'
                }`}
              >
                Split
              </button>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <button
              onClick={handleMinify}
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors font-medium"
            >
              <Play className="w-4 h-4" />
              <span>Run</span>
            </button>
            
            <button
              onClick={handleUpload}
              className="bg-blue-600 hover:bg-blue-700 text-white p-2 rounded-lg transition-colors"
              title="Upload CSS file"
            >
              <Upload className="w-4 h-4" />
            </button>
            
            <button
              onClick={handleDownload}
              disabled={!outputCSS}
              className="bg-gray-700 hover:bg-gray-600 disabled:bg-gray-800 disabled:cursor-not-allowed text-white p-2 rounded-lg transition-colors"
              title="Download minified CSS"
            >
              <Download className="w-4 h-4" />
            </button>
            
            <button
              onClick={handleCopy}
              disabled={!outputCSS}
              className="bg-gray-700 hover:bg-gray-600 disabled:bg-gray-800 disabled:cursor-not-allowed text-white p-2 rounded-lg transition-colors"
              title="Copy to clipboard"
            >
              <Copy className="w-4 h-4" />
            </button>
            
            <button
              onClick={handleSave}
              className="bg-gray-700 hover:bg-gray-600 text-white p-2 rounded-lg transition-colors"
              title="Save to local storage"
            >
              <Save className="w-4 h-4" />
            </button>
            
            <button
              onClick={handleReset}
              className="bg-gray-700 hover:bg-gray-600 text-white p-2 rounded-lg transition-colors"
              title="Reset all"
            >
              <RotateCcw className="w-4 h-4" />
            </button>
            
            <button
              className="bg-gray-700 hover:bg-gray-600 text-white p-2 rounded-lg transition-colors"
              title="Settings"
            >
              <Settings className="w-4 h-4" />
            </button>
            
            <button
              onClick={() => setIsFullscreen(!isFullscreen)}
              className="bg-gray-700 hover:bg-gray-600 text-white p-2 rounded-lg transition-colors"
              title="Toggle fullscreen"
            >
              {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
            </button>
          </div>
        </div>
      </div>

      {/* Code Issues */}
      {codeIssues.length > 0 && (
        <div className="bg-gray-900 border-b border-gray-700 px-6 py-3">
          <div className="flex items-center space-x-2 text-yellow-400">
            <AlertCircle className="w-4 h-4" />
            <span className="text-sm font-medium">Code Issues ({codeIssues.length})</span>
          </div>
          <div className="mt-2 space-y-1">
            {codeIssues.slice(0, 3).map((issue, index) => (
              <div key={index} className="flex items-center space-x-2 text-sm">
                <span className="bg-blue-600 text-xs px-2 py-1 rounded">info</span>
                <span className="text-gray-400">Line {issue.line}:</span>
                <span className="text-gray-300">{issue.message}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Editor Status Bar */}
      <div className="bg-gray-800 border-b border-gray-700 px-6 py-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-6 text-sm text-gray-400">
            <div className="flex items-center space-x-2">
              <ChevronDown className="w-4 h-4" />
              <span className="text-white">CSS Editor</span>
              <span className="text-green-400">✓ Auto-saved</span>
            </div>
            <span>{lineCount} lines</span>
            <span>{charCount} chars</span>
            <span>{wordCount} words</span>
            {compressionRatio > 0 && (
              <>
                <span>Original: {formatBytes(originalSize)}</span>
                <span>Minified: {formatBytes(minifiedSize)}</span>
                <span className="text-green-400">Saved: {compressionRatio}%</span>
              </>
            )}
          </div>
          <div className="flex items-center space-x-2 text-sm text-gray-400">
            <span>Live Preview</span>
            <div className="w-2 h-2 bg-green-400 rounded-full"></div>
            <span className="text-green-400">Live</span>
          </div>
        </div>
      </div>

      {/* Minification Options - Fixed Position */}
      <div className="bg-gray-800 border-b border-gray-700 px-6 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <h3 className="text-sm font-medium text-white">Minification Options:</h3>
            <div className="flex flex-wrap gap-4">
              {Object.entries(options).map(([key, value]) => (
                <label key={key} className="flex items-center space-x-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={value}
                    onChange={(e) => setOptions(prev => ({ ...prev, [key]: e.target.checked }))}
                    className="w-4 h-4 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500 focus:ring-2"
                  />
                  <span className="text-gray-300">
                    {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                  </span>
                </label>
              ))}
            </div>
          </div>
          
          <div className="flex items-center space-x-4 text-sm text-gray-400">
            <div className="flex items-center space-x-2">
              <Shield className="w-4 h-4" />
              <span>Secure Processing</span>
            </div>
            <div className="flex items-center space-x-2">
              <Zap className="w-4 h-4" />
              <span>Real-time</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex" style={{ height: 'calc(100vh - 280px)' }}>
        {/* Left Pane - Input */}
        {(viewMode === 'code' || viewMode === 'split') && (
          <div className="flex-1 flex flex-col bg-gray-900 border-r border-gray-700">
            <div className="flex-1 relative">
              <div className="absolute inset-0 flex">
                {/* Line numbers */}
                <div className="w-12 bg-gray-800 border-r border-gray-700 text-right py-4 text-sm text-gray-500 font-mono leading-6 overflow-hidden">
                  {inputCSS.split('\n').map((_, index) => (
                    <div key={index} className="px-2 h-6">
                      {index + 1}
                    </div>
                  ))}
                </div>
                
                {/* Code editor */}
                <div className="flex-1">
                  <textarea
                    value={inputCSS}
                    onChange={(e) => setInputCSS(e.target.value)}
                    className="w-full h-full bg-transparent text-white font-mono text-sm leading-6 p-4 resize-none focus:outline-none"
                    placeholder="Enter your CSS code here..."
                    spellCheck={false}
                    style={{ 
                      tabSize: 2,
                      fontFamily: 'JetBrains Mono, Consolas, Monaco, monospace'
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Right Pane - Output/Preview */}
        {(viewMode === 'preview' || viewMode === 'split') && (
          <div className="flex-1 flex flex-col bg-gray-900">
            <div className="flex-1 relative">
              <div className="absolute inset-0 flex">
                {/* Line numbers */}
                <div className="w-12 bg-gray-800 border-r border-gray-700 text-right py-4 text-sm text-gray-500 font-mono leading-6 overflow-hidden">
                  {outputCSS.split('\n').map((_, index) => (
                    <div key={index} className="px-2 h-6">
                      {index + 1}
                    </div>
                  ))}
                </div>
                
                {/* Output display */}
                <div className="flex-1">
                  <pre className="w-full h-full bg-transparent text-gray-300 font-mono text-sm leading-6 p-4 overflow-auto whitespace-pre-wrap break-words">
                    {outputCSS || '// Minified CSS will appear here after running the minifier...'}
                  </pre>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".css,text/css"
        onChange={handleFileChange}
        className="hidden"
      />
    </div>
  );
};

export default CSSMinifier;